// src/components/layout/UserMenu.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getCurrentUser, isSignedIn, updateAuthUser } from "../../utils/auth";
import { usePermissions } from "../../utils/permissions";
import {
  buildDefaultPageOptions,
  resolveDefaultRoute,
  SAFE_FALLBACK_ROUTE,
} from "../../utils/defaultPage";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.portpilot.co";

/** �_?����s,��?�%؇"��"Y�.c����,r�_���^�"_�?'�,�--/��݊TY�^+�s"��% */
function makeInitials(name, email) {
  const local = (email || "").split("@")[0] || "";
  const source = (name || "").trim() || local || "user";
  const tokens = source.split(/[^A-Za-z0-9]+/).filter(Boolean);

  let a = "", b = "";
  if (tokens.length >= 2) {
    a = tokens[0][0];
    b = tokens[tokens.length - 1][0];
  } else {
    const t = tokens[0] || "";
    if (t) {
      a = t[0];
      for (let i = 1; i < t.length; i++) {
        if (/[A-Za-z0-9]/.test(t[i])) { b = t[i]; break; }
      }
    }
    if (!b) {
      const fallback = (local.replace(/[^A-Za-z0-9]/g, "") || "US");
      b = fallback[1] || fallback[0] || "U";
    }
  }
  return (String(a) + String(b)).toUpperCase();
}

function LabelValue({ label, value }) {
  return (
    <div className="col-span-1">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-sm">{value || <span className="text-gray-400">-</span>}</div>
    </div>
  );
}

export default function UserMenu() {
  const me = getCurrentUser();
  const navigate = useNavigate();
  const { getLevel } = usePermissions();
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showPw, setShowPw] = useState(false);
  const panelRef = useRef(null);
  const defaultMenuRef = useRef(null);

  const [pageOptions, setPageOptions] = useState(() => buildDefaultPageOptions(getLevel, me?.role));
  const [defaultPageId, setDefaultPageId] = useState(() => me?.default_page_id || "");
  const [defaultMenuOpen, setDefaultMenuOpen] = useState(false);
  const [defaultSaving, setDefaultSaving] = useState(false);

  const initials = makeInitials(me?.name, me?.email);
  const selectedPageLabel = useMemo(() => {
    if (!defaultPageId) return "";
    const match = pageOptions.find((opt) => opt.id === defaultPageId);
    return match ? match.label : "";
  }, [defaultPageId, pageOptions]);
  const recommendedRoute = useMemo(() => {
    if (!me?.email) return "";
    return resolveDefaultRoute({
      user: me,
      role: me?.role,
      getLevel,
      userDefaultPageId: me?.default_page_id,
      roleDefaultPageId: me?.role_default_page_id,
      allowedPagesOverride: pageOptions,
    });
  }, [me, pageOptions, getLevel]);
  const recommendedOption = useMemo(
    () => pageOptions.find((opt) => opt.route === recommendedRoute),
    [pageOptions, recommendedRoute]
  );

  useEffect(() => {
    setPageOptions(buildDefaultPageOptions(getLevel, me?.role));
  }, [getLevel, me?.role]);

  useEffect(() => {
    if (!me?.email) {
      setDefaultPageId("");
      return;
    }
    const allowedIds = new Set(pageOptions.map((opt) => opt.id));
    if (me?.default_page_id && allowedIds.has(me.default_page_id)) {
      setDefaultPageId(me.default_page_id);
      return;
    }
    if (!me?.default_page_id || allowedIds.size === 0) {
      setDefaultPageId("");
      return;
    }
    if (!allowedIds.has(me.default_page_id)) {
      setDefaultPageId("");
    }
  }, [me?.email, me?.default_page_id, pageOptions]);

  useEffect(() => {
    function onDoc(e) {
      if (!open) return;
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
      if (defaultMenuRef.current && !defaultMenuRef.current.contains(e.target)) setDefaultMenuOpen(false);
    }
    function onKey(e) {
      if (open && e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open) setDefaultMenuOpen(false);
  }, [open]);

  if (!isSignedIn() || !me) return null;

  async function changePassword() {
    if (!pw.trim()) { setErr("Password is required."); return; }
    setBusy(true);
    setErr("");
    try {
      const headers = {
        "Content-Type": "application/json",
        "X-User-Email": me.email || "",
        "X-User-Role": me.role || "",
      };
      const r = await fetch(`${API_BASE}/users/me`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ password: pw.trim() }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) throw new Error(j.error || "Change password failed");
      setPw("");
      setOpen(false);
      alert("Password updated.");
    } catch (e) {
      setErr(e.message || "Change password failed.");
    } finally {
      setBusy(false);
    }
  }

  async function persistDefaultPage(pageId) {
    if (!me?.email) return;
    if (defaultSaving) return;
    setDefaultSaving(true);
    try {
      const headers = {
        "Content-Type": "application/json",
        "X-User-Email": me.email || "",
        "X-User-Role": me.role || "",
      };
      const resp = await fetch(`${API_BASE}/api/me/default-page`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ default_page_id: pageId }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const message = data?.message || data?.error || "Failed to update default page.";
        if (resp.status === 403) {
          window.alert(message || "You don't have access to this page.");
        } else {
          window.alert(message || "Failed to update default page.");
        }
        return;
      }
      const nextPageId = data?.user_default_page_id ?? pageId ?? null;
      const sanitizedId = nextPageId || "";
      setDefaultPageId(sanitizedId);
      updateAuthUser((user) => ({
        ...user,
        default_page_id: nextPageId || null,
        role_default_page_id: data?.role_default_page_id ?? user.role_default_page_id,
        landing_route: data?.landing_route || user.landing_route,
      }));
      const nextUserSnapshot = {
        ...me,
        default_page_id: nextPageId || null,
        role_default_page_id: data?.role_default_page_id ?? me?.role_default_page_id,
      };
      const fallbackRoute =
        resolveDefaultRoute({
          user: nextUserSnapshot,
          role: nextUserSnapshot.role,
          getLevel,
          userDefaultPageId: nextUserSnapshot.default_page_id,
          roleDefaultPageId: nextUserSnapshot.role_default_page_id,
          allowedPagesOverride: pageOptions,
        }) || SAFE_FALLBACK_ROUTE;
      const targetRoute = data?.landing_route || fallbackRoute;
      setDefaultMenuOpen(false);
      if (targetRoute) {
        navigate(targetRoute);
      }
    } catch (error) {
      console.error(error);
      window.alert("Failed to update default page. Please try again.");
    } finally {
      setDefaultSaving(false);
    }
  }

  function handleDefaultSelect(pageId) {
    if (!pageId) return;
    if (!pageOptions.some((opt) => opt.id === pageId)) {
      window.alert("You don't have access to this page.");
      return;
    }
    void persistDefaultPage(pageId);
  }

  function handleDefaultClear() {
    void persistDefaultPage(null);
  }

  return (
    <div className="relative" data-pp-user-menu-root>
      <button
        type="button"
        data-pp-user-menu
        onClick={() => setOpen((v) => !v)}
        title={me?.email || "User"}
        className="h-9 w-9 rounded-full bg-teal-500 text-white text-sm font-bold grid place-items-center shadow hover:brightness-110 focus:outline-none"
        aria-haspopup="menu"
        aria-expanded={open ? "true" : "false"}
      >
        {initials}
      </button>

      {open && (
        <div
          ref={panelRef}
          role="menu"
          className="absolute left-1/4 -translate-x-1 bottom-12 w-80 rounded-xl border bg-white p-4 shadow-lg z-[400]"
        >
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-teal-500 text-white grid place-items-center font-bold">
              {initials}
            </div>
            <div className="min-w-0">
              <div className="font-semibold truncate">{me?.name || "-"}</div>
              <div className="text-xs text-gray-500 truncate">{me?.email}</div>
            </div>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
            <LabelValue label="Role" value={me?.role} />
            <LabelValue label="Company" value={me?.company} />
            <LabelValue label="Company Code" value={me?.company_code} />
            <LabelValue label="Remark" value={me?.remark} />
          </div>

          <div className="mt-4">
            <div className="mb-1 text-sm font-medium">Default Page</div>
            {pageOptions.length > 0 ? (
              <div className="relative" ref={defaultMenuRef}>
                <button
                  type="button"
                  onClick={() => setDefaultMenuOpen((v) => !v)}
                  className="flex w-full items-center justify-between rounded border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                >
                  <span className="truncate text-left">
                    {selectedPageLabel || "Select a page"}
                  </span>
                  <svg
                    viewBox="0 0 20 20"
                    className={`h-4 w-4 text-gray-500 transition-transform ${defaultMenuOpen ? "rotate-180" : ""}`}
                  >
                    <path
                      d="M6 12l4-4 4 4"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                {defaultMenuOpen && (
                  <div className="absolute bottom-full left-0 z-[450] mb-1 w-full rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                    <div className="max-h-48 overflow-y-auto text-sm">
                      {pageOptions.map((option) => (
                        <button
                          type="button"
                          key={option.id}
                          onClick={() => handleDefaultSelect(option.id)}
                          disabled={defaultSaving}
                          className={`flex w-full items-center justify-between px-3 py-2 text-left hover:bg-blue-50 ${
                            option.id === defaultPageId ? "text-blue-600" : "text-gray-700"
                          }`}
                        >
                          <span className="truncate">{option.label}</span>
                          {option.id === defaultPageId && (
                            <span className="ml-2 text-xs font-medium uppercase">Selected</span>
                          )}
                        </button>
                      ))}
                    </div>
                    <div className="border-t border-gray-100 px-3 py-2 text-xs text-gray-500 space-y-1">
                      {recommendedOption ? (
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate">
                            Recommended: {recommendedOption.label}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleDefaultSelect(recommendedOption.id)}
                            disabled={defaultSaving}
                            className="text-blue-600 whitespace-nowrap hover:underline disabled:opacity-50"
                          >
                            {defaultSaving ? "Saving..." : "Use recommended page"}
                          </button>
                        </div>
                      ) : (
                        <div className="text-gray-400">Recommended page unavailable.</div>
                      )}
                      <button
                        type="button"
                        onClick={handleDefaultClear}
                        disabled={defaultSaving}
                        className="text-blue-600 hover:underline disabled:opacity-50"
                      >
                        {defaultSaving ? "Saving..." : "Clear default"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded border border-dashed border-gray-300 px-3 py-2 text-xs text-gray-400">
                No accessible pages detected for this role.
              </div>
            )}
          </div>

          <div className="mt-5">
            <div className="mb-1 text-sm font-medium">Change Password</div>
            <div className="relative">
              <input
                type={showPw ? "text" : "password"}
                className="w-full rounded border px-2 py-2 pr-16 text-sm"
                placeholder="New password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-blue-600 hover:underline"
                aria-label={showPw ? "Hide password" : "Show password"}
              >
                {showPw ? "Hide" : "Show"}
              </button>
            </div>

            {err && <div className="mt-1 text-xs text-red-600">{err}</div>}

            <div className="mt-3 flex justify-end">
              <button
                type="button"
                onClick={changePassword}
                disabled={busy}
                className="rounded bg-blue-600 px-3 py-1.5 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {busy ? "Saving..." : "Update Password"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
